import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '../../util/logger';

interface ModalSize {
  width: number;
  height: number;
}

interface UseModalSizeObserverProps {
  modalRef: React.RefObject<HTMLIonModalElement>;
  isOpen: boolean;
  isFullScreen?: boolean;
  canGoFullScreen: boolean;
}

const MODAL_WRAPPER_TIMEOUT_MS = 5000;

const useModalSizeObserver = ({
  modalRef,
  isOpen,
  isFullScreen = false,
  canGoFullScreen,
}: UseModalSizeObserverProps): ModalSize => {
  const [initialModalSize, setInitialModalSize] = useState<ModalSize>({
    width: 0,
    height: 0,
  });

  const modalResizeObserverRef = useRef<ResizeObserver | null>(null);
  const modalWrapperObserverRef = useRef<MutationObserver | null>(null);
  const timeoutIdRef = useRef<number | null>(null);

  const cleanupWrapperResources = useCallback(() => {
    if (modalWrapperObserverRef.current) {
      modalWrapperObserverRef.current.disconnect();
      modalWrapperObserverRef.current = null;
    }
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const cleanupAllResources = useCallback(() => {
    if (modalResizeObserverRef.current) {
      modalResizeObserverRef.current.disconnect();
      modalResizeObserverRef.current = null;
    }
    cleanupWrapperResources();
  }, [cleanupWrapperResources]);

  useEffect(() => {
    if (!canGoFullScreen || !isOpen) {
      cleanupAllResources();
      return;
    }

    const modalNode = modalRef.current;
    const isModalSizeSet = initialModalSize.width > 0;

    if (!isFullScreen && !isModalSizeSet && modalNode?.shadowRoot) {
      const setupResizeObserver = (wrapperNode: HTMLElement) => {
        if (modalResizeObserverRef.current) {
          modalResizeObserverRef.current.disconnect();
        }

        const observer = new ResizeObserver((entries) => {
          const entry = entries[0];
          const width =
            entry.contentRect?.width ?? wrapperNode?.offsetWidth ?? 0;
          const height =
            entry.contentRect?.height ?? wrapperNode?.offsetHeight ?? 0;

          if (width > 0 && height > 0) {
            if (!isFullScreen && initialModalSize.width === 0) {
              setInitialModalSize({ width, height });
            }
            observer.disconnect();
            modalResizeObserverRef.current = null;
          }
        });

        observer.observe(wrapperNode);
        modalResizeObserverRef.current = observer;
      };

      // First, try to find the wrapper node immediately
      const wrapperNode =
        modalNode.shadowRoot.querySelector<HTMLElement>('.modal-wrapper');

      if (wrapperNode) {
        setupResizeObserver(wrapperNode);
      } else {
        // If not found, use MutationObserver to watch for it
        if (modalWrapperObserverRef.current) {
          modalWrapperObserverRef.current.disconnect();
        }

        const mutationObserver = new MutationObserver(() => {
          const wrapperNode =
            modalNode.shadowRoot?.querySelector<HTMLElement>('.modal-wrapper');
          if (wrapperNode) {
            setupResizeObserver(wrapperNode);
            cleanupWrapperResources();
          }
        });

        mutationObserver.observe(modalNode.shadowRoot, {
          childList: true,
          subtree: true,
        });

        modalWrapperObserverRef.current = mutationObserver;

        // Cleanup after a reasonable timeout to prevent memory leaks
        timeoutIdRef.current = window.setTimeout(() => {
          cleanupAllResources();
          logger.warn(
            `ModalWrapperObserver timeout - modal wrapper not found within ${MODAL_WRAPPER_TIMEOUT_MS}ms`
          );
        }, MODAL_WRAPPER_TIMEOUT_MS);
      }
    }

    return cleanupAllResources;
  }, [
    isOpen,
    isFullScreen,
    canGoFullScreen,
    initialModalSize.width,
    modalRef,
    cleanupAllResources,
    cleanupWrapperResources,
  ]);

  useEffect(() => {
    if (!canGoFullScreen) {
      return;
    }
    if (!isOpen) {
      setInitialModalSize({ width: 0, height: 0 });
    }
  }, [isOpen, canGoFullScreen]);

  return initialModalSize;
};

export { useModalSizeObserver };
